import {
  Alert,
  Badge,
  Box,
  Button,
  Card,
  Group,
  Modal,
  MultiSelect,
  Stack,
  Switch,
  Textarea,
  Text,
  TextInput,
  Title,
} from '@mantine/core';
import { useDisclosure } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { IconAlertTriangle } from '@tabler/icons-react';
import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { parseMutationError } from '../api/errors';
import { useOrgs } from '../api/orgs';
import { usePermissions } from '../api/permissions';
import { useDeleteRole, useRole, useUpdateRole } from '../api/roles';

export function RoleEditPage() {
  const { id = '' } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const roleQuery = useRole(id);
  const update = useUpdateRole(id);
  const remove = useDeleteRole(id);
  const permissions = usePermissions();
  const { data: orgs } = useOrgs();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isDefault, setIsDefault] = useState(false);
  const [permissionIds, setPermissionIds] = useState<string[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [confirmOpen, { open: openConfirm, close: closeConfirm }] = useDisclosure(false);

  const role = roleQuery.data;

  useEffect(() => {
    if (!role) return;
    setName(role.name);
    setDescription(role.description ?? '');
    setIsDefault(role.is_default);
    setPermissionIds(role.permissions.map((p) => p.id));
  }, [role?.id]);

  if (roleQuery.isPending) return <Text p="md">Loading…</Text>;
  if (roleQuery.isError || !role) return <Text p="md">Could not load role.</Text>;

  const orgName =
    (orgs ?? []).find((o) => o.id === role.organisation_id)?.name ?? role.organisation_id;

  const dirty =
    role.name !== name.trim() ||
    (role.description ?? '') !== description.trim() ||
    role.is_default !== isDefault ||
    permissionIds.slice().sort().join(',') !==
      role.permissions
        .map((p) => p.id)
        .slice()
        .sort()
        .join(',');

  function handleSave(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setError(null);
    update.mutate(
      {
        name: name.trim(),
        description: description.trim() || undefined,
        is_default: isDefault,
        permission_ids: permissionIds,
      },
      {
        onSuccess: () => notifications.show({ color: 'green', message: 'Role updated.' }),
        onError: (err) => setError(parseMutationError(err, 'Could not save changes.')),
      },
    );
  }

  function handleDelete() {
    remove.mutate(undefined, {
      onSuccess: () => {
        notifications.show({ color: 'green', message: 'Role deleted.' });
        navigate(`/roles?org=${role!.organisation_id}`);
      },
      onError: (err) => {
        setError(parseMutationError(err, 'Could not delete role.'));
        closeConfirm();
      },
    });
  }

  return (
    <Stack gap="lg">
      <Group justify="space-between" align="flex-end">
        <Title order={2}>{role.name}</Title>
        <Button variant="subtle" onClick={() => navigate(`/roles?org=${role.organisation_id}`)}>
          Back to roles
        </Button>
      </Group>

      {error && (
        <Alert color="red" icon={<IconAlertTriangle size={18} />}>
          {error}
        </Alert>
      )}

      <Card withBorder padding="lg" maw={680}>
        <Group gap="lg">
          <div>
            <Text size="sm" c="dimmed">
              Organisation
            </Text>
            <Text fw={500}>{orgName}</Text>
          </div>
          <div>
            <Text size="sm" c="dimmed">
              Members
            </Text>
            <Badge color={role.member_count > 0 ? 'blue' : 'gray'} variant="light">
              {role.member_count} user{role.member_count === 1 ? '' : 's'}
            </Badge>
          </div>
        </Group>
      </Card>

      <Card withBorder padding="lg" maw={680}>
        <Box component="form" onSubmit={handleSave}>
          <Stack gap="md">
            <Title order={5}>Profile</Title>
            <TextInput
              label="Name"
              required
              value={name}
              onChange={(e) => setName(e.currentTarget.value)}
              disabled={update.isPending}
            />
            <Textarea
              label="Description"
              value={description}
              onChange={(e) => setDescription(e.currentTarget.value)}
              disabled={update.isPending}
              minRows={2}
              autosize
            />
            <Switch
              label="Mark as the org's default role"
              checked={isDefault}
              onChange={(e) => setIsDefault(e.currentTarget.checked)}
              disabled={update.isPending}
            />
            <MultiSelect
              label="Permissions"
              searchable
              clearable
              value={permissionIds}
              onChange={setPermissionIds}
              data={(permissions.data ?? []).map((p) => ({
                value: p.id,
                label: p.permission,
              }))}
              disabled={update.isPending}
            />
            <Group justify="flex-end">
              <Button type="submit" loading={update.isPending} disabled={!dirty}>
                Save changes
              </Button>
            </Group>
          </Stack>
        </Box>
      </Card>

      <Card withBorder padding="lg" maw={680}>
        <Stack gap="sm">
          <Title order={5}>Danger zone</Title>
          <Group>
            <Button color="red" variant="subtle" onClick={openConfirm}>
              Delete role
            </Button>
          </Group>
        </Stack>
      </Card>

      <Modal
        opened={confirmOpen}
        onClose={closeConfirm}
        title="Delete role"
        transitionProps={{ duration: 0 }}
      >
        <Stack>
          {role.member_count > 0 ? (
            <>
              <Text>
                Cannot delete: <strong>{role.member_count}</strong> user
                {role.member_count === 1 ? '' : 's'} currently{' '}
                {role.member_count === 1 ? 'has' : 'have'} this role. Reassign them first.
              </Text>
              <Group justify="flex-end">
                <Button variant="subtle" onClick={closeConfirm}>
                  Close
                </Button>
              </Group>
            </>
          ) : (
            <>
              <Text>
                Delete role <strong>{role.name}</strong>? This is irreversible.
              </Text>
              <Group justify="flex-end">
                <Button variant="subtle" onClick={closeConfirm}>
                  Cancel
                </Button>
                <Button color="red" onClick={handleDelete} loading={remove.isPending}>
                  Delete
                </Button>
              </Group>
            </>
          )}
        </Stack>
      </Modal>
    </Stack>
  );
}
